import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TeacherFile } from '@isophi/pap-api';

import { ActionItem } from '../../constants/types';
import { BaseComponent } from '../base-component/base.component';
import { DocumentPreviewService } from '../../services/document-preview.service';
import { RenameFileModalComponent } from '../rename-file-modal/rename-file-modal.component';
import { PRINT_DOWNLOAD_ICON } from '../print-download-icon/print-download-icon.component';

export interface RenameDocumentEvent {
  documentId: string;
  title: string;
}

@Component({
    selector: 'isophi-portfolio-plan-documents',
    templateUrl: './portfolio-plan-documents.component.html',
    styleUrls: ['./portfolio-plan-documents.component.scss'],
    standalone: false
})
export class PortfolioPlanDocumentsComponent extends BaseComponent {
  @Input() allowDownload = false;
  @Input() documents: TeacherFile[] = [];

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  @Output() createDocument = new EventEmitter<File>();
  @Output() deleteDocument = new EventEmitter<string>();
  @Output() renameDocument = new EventEmitter<RenameDocumentEvent>();

  @Output() downloadDocument = new EventEmitter<string>();

  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(NgbModal);
  private readonly documentPreviewService = inject(DocumentPreviewService);

  actionItems: ActionItem[] = [
    {
      name: 'print',
      label: this.translocoService.translate('shared/printDownload'),
      icon: PRINT_DOWNLOAD_ICON,
    },
    {
      name: 'rename',
      label: this.translocoService.translate('portfolio/rename'),
      icon: 'edit',
    },
    {
      name: 'delete',
      label: this.translocoService.translate('portfolio/delete'),
      icon: 'delete',
    },
  ];

  openFileInput(): void {
    this.fileInputRef.nativeElement.click();
  }

  onRightIconClick(): void {
    this.openFileInput();
  }

  handleFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.createTeacherActivityDocument(file);
  }

  onAction(action: ActionItem, document: TeacherFile): void {
    switch (action.name) {
      case 'print':
        this.openPreview(document);
        break;
      case 'rename':
        this.renameTeacherActivityDocument(document);
        break;
      case 'delete':
        this.deleteTeacherActivityDocument(document.id);
        break;
    }
  }

  openPreview(document: TeacherFile): void {
    this.documentPreviewService.openUrl(document.file, document.title);
  }

  private createTeacherActivityDocument(file: File): void {
    this.createDocument.emit(file);
  }

  private async renameTeacherActivityDocument(document: TeacherFile): Promise<void> {
    const title = await RenameFileModalComponent.open(this.modalService, document);
    if (title !== null && title !== document.title) {
      this.renameDocument.emit({ documentId: document.id, title });
    }
  }

  private deleteTeacherActivityDocument(documentId: string): void {
    this.deleteDocument.emit(documentId);
  }
}
